import { Box, SxProps } from '@mui/material';
import debugFn from 'debug';
import { useEffect, useMemo, useRef } from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { setupVideoJsPlayerTech } from './setup-video-js-player-tech';
import './styles.css';
import { useVideoCapture } from './useVideoCapture';
import './vod-current-time';
import './vod-time-tooltip';

const debug = debugFn('VideoJsPlayer');

export type VideoJsPlayerOptions = Record<string, unknown>;

export interface VideoJsPlayerProps {
  src: string;
  options?: VideoJsPlayerOptions;
  onReady?: (player: Player) => void;
  isLive?: boolean;
  playerClasses?: string[];
  playerAttrs?: Record<string, string>;
  enableSnapshotPlugin?: boolean;
  sx?: SxProps;
  bordered?: boolean;
}

const DEFAULT_OPTIONS = {
  preload: 'auto',
  autoplay: false,
  controls: true,
  volume: 1,
  muted: false,
  userActions: {
    doubleClick: false,
  },
  liveTracker: false,
  fluid: true,
};

export const VideoJsPlayer = (props: VideoJsPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const unregisterCaptureRef = useRef<(() => void) | null>(null);
  const registerVideoCapture = useVideoCapture();

  const options = useMemo(() => {
    return {
      ...DEFAULT_OPTIONS,
      ...props.options,
    };
  }, [JSON.stringify(props.options)]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');

      videoElement.classList.add('video-js-new');
      // videoElement.classList.add('vjs-has-started');

      props.playerClasses?.forEach((cls) => videoElement.classList.add(cls));
      Object.entries(props.playerAttrs ?? {}).forEach(([key, value]) =>
        videoElement.setAttribute(key, value),
      );
      videoElement.setAttribute('crossorigin', 'anonymous');

      if (props.isLive) {
        // Force the "Live" UI class to stay active
        videoElement.classList.add('vjs-live');
      }

      container.appendChild(videoElement);

      debug('initialize videojs player', options);
      const player = videojs(videoElement, options, function (this: Player) {
        if (props.onReady) {
          props.onReady(this);
        }
      });

      if (props.enableSnapshotPlugin) {
        (player as unknown as { snapshotPlugin: () => void }).snapshotPlugin();
      }

      setupVideoJsPlayerTech(player, props.src, {
        isLive: props.isLive,
      });

      playerRef.current = player;
      unregisterCaptureRef.current?.();
      unregisterCaptureRef.current = registerVideoCapture(player);
    }

    return () => {
      unregisterCaptureRef.current?.();
      unregisterCaptureRef.current = null;

      const player = playerRef.current;

      if (player && !player.isDisposed()) {
        debug('unmount, dispose player');
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [props.src, registerVideoCapture]);

  return (
    <Box
      data-vjs-player
      sx={[
        {
          // Parent container constraints
          display: 'flex', // Ensure this behaves as a flex container
          flex: 1,
          position: 'relative',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden', // Safety net for child overflow
        },
        ...(props.bordered
          ? [
              {
                border: '2px solid #444',
                borderRadius: '4px',
              },
            ]
          : []),
        ...(Array.isArray(props.sx) ? props.sx : [props.sx ?? {}]),
      ]}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};
